import { CNavItem } from '@coreui/react-pro'
import { NavLink, useHistory } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import { useAppDispatch, useTypedSelector } from '../../stateStore'
import { reduxServices } from '../../reducers/reduxServices'
import { baseImageExtension } from '../../pages/Achievements/AchievementConstants'
import { LoadingType } from '../../types/Components/loadingScreenTypes'
import OLoadingSpinner from '../ReusableComponent/OLoadingSpinner'
import {
  EmployeeGeneralInformation,
  VendorGeneralInformation,
} from '../../types/MyProfile/GeneralTab/generalInformationTypes'
import { asyncApiRetryHelper } from '../../utils/asyncApiRetryUtils'
import usePermissionToView, {
  PermissionEnum,
} from '../../middleware/hooks/usePermissionToView'

const UserProfile = ({ fromLocal }: { fromLocal: string }): JSX.Element => {
  usePermissionToView('My Profile', PermissionEnum.View)
  const history = useHistory()
  const sidebarShow = useTypedSelector(
    reduxServices.app.selectors.getSidebarShow,
  )
  const isUpdatedLoggedIn = useTypedSelector(
    reduxServices.basicInformation.selectors.isUpdateLoggedIn,
  )
  const vendorName = localStorage.getItem('vendorUserName')
  const [employeeGeneralInformation, setEmployeeGeneralInformation] =
    useState<EmployeeGeneralInformation>()
  const dispatch = useAppDispatch()
  const isVendor = localStorage.getItem('isVendor') === 'true'
  const fetchDataCheck = () => {
    if (!isVendor) {
      return fetchData
    } else {
      return fetchVendorData
    }
  }
  const fetchData = async (): Promise<boolean> => {
    try {
      const employeeGeneralInformationTest = await dispatch(
        reduxServices.generalInformation.getEmployeeGeneralInformation(
          // this is the endpoint that is being called
          fromLocal,
        ),
      )

      if (employeeGeneralInformationTest.payload === 403) {
        history.push('/forbidden')
        return false
      }

      setEmployeeGeneralInformation(
        employeeGeneralInformationTest?.payload as unknown as EmployeeGeneralInformation,
      )

      const rawPayload = employeeGeneralInformationTest?.payload as unknown
      let workLocation: string | undefined
      if (rawPayload && typeof rawPayload === 'object') {
        const rec = rawPayload as Record<string, unknown>
        const candidate =
          rec['workLocation'] ?? rec['baseLocation'] ?? rec['curentLocation']
        if (typeof candidate === 'string' && candidate.trim() !== '') {
          workLocation = candidate
        }
      }

      if (workLocation) {
        localStorage.setItem('workLocation', workLocation)
        if (workLocation !== 'N/A') {
          localStorage.setItem('previousWorkLocation', workLocation)
        }
      } else {
        localStorage.removeItem('workLocation')
      }

      return employeeGeneralInformationTest.meta.requestStatus === 'fulfilled'
    } catch (error) {
      console.error('Error fetching data:', error)
      return false
    }
  }
  const fetchVendorData = async (): Promise<boolean> => {
    try {
      const vendorGeneralInformationTest = await dispatch(
        reduxServices.generalInformation.getVendorGeneralInformation(
          // this is the endpoint that is being called
          fromLocal,
        ),
      )

      if (vendorGeneralInformationTest.payload === 403) {
        history.push('/forbidden')
        return false
      }

      setEmployeeGeneralInformation(
        vendorGeneralInformationTest?.payload as unknown as VendorGeneralInformation,
      )

      // Vendor data does not include workLocation; ensure no stale value remains
      localStorage.removeItem('workLocation')

      return vendorGeneralInformationTest.meta.requestStatus === 'fulfilled'
    } catch (error) {
      console.error('Error fetching data:', error)
      return false
    }
  }

  const isLoadingEdit = useTypedSelector(
    reduxServices.generalInformation.selectors.isLoading,
  )
  const isUpdated = useTypedSelector(
    reduxServices.basicInformation.selectors.isUpdated,
  )

  if (isUpdated && isUpdatedLoggedIn) {
    asyncApiRetryHelper(fetchDataCheck())
    dispatch(reduxServices.basicInformation.actions.setIsUpdated(false))
    dispatch(reduxServices.basicInformation.actions.setIsUpdatedLoggedIn(false))
  }

  useEffect(() => {
    asyncApiRetryHelper(fetchDataCheck())

    return () => {
      setEmployeeGeneralInformation({} as EmployeeGeneralInformation)
    }
  }, [])

  return (
    <>
      {isLoadingEdit && <OLoadingSpinner type={LoadingType.PAGE} />}
      <div className="user-profile-wrap">
        <CNavItem>
          <NavLink to="/profile" className="user-profile-link">
            <img
              width={120}
              height={120}
              className="user-profile-img"
              key={new Date().getTime()}
              src={
                isVendor
                  ? 'https://ovh2.raybiztech.com/profilepics/Default_Male.jpg'
                  : `${baseImageExtension}${
                      employeeGeneralInformation?.profilePicPath
                    }?t=${Date.now()}`
              }
              alt={employeeGeneralInformation?.fullName ?? 'User Profile'}
            />
          </NavLink>
        </CNavItem>

        {sidebarShow && (
          <div className="user-profile-text">
            <h4>
              <span className="user-profile-name">
                {isVendor ? vendorName : employeeGeneralInformation?.fullName}
              </span>
            </h4>
            <h5>
              <span className="user-profile-designation">
                {isVendor
                  ? localStorage.getItem('vendorName')
                  : employeeGeneralInformation?.designation}
              </span>
            </h5>
          </div>
        )}
      </div>
    </>
  )
}

export default UserProfile
